import { Box, Checkbox, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { CardWithContent } from 'src/components/molecules/card-with-content';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { CompactPagination } from 'src/components/organisms/compact-pagination';
import { fetchEmployeesGroups } from 'src/features/employees-groups/api/lib/employees-groups';
import { employeesGroupsKeys } from 'src/features/employees-groups/api/query-keys/employees-groups';
import { useAssignEmployeeGroupsToSpaceGroup } from 'src/features/space-groups/api/hooks/use-assign-employee-groups-to-space-group';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { usePagination } from 'src/hooks/use-pagination';
import { useSearchState } from 'src/hooks/use-search-state';
import { useUserStore } from 'src/store/user-store';
import { SpaceGroupsActionsSlotPayload } from '../../molecules/space-groups-table-row';

interface Props extends DialogActions {
  selectedSpaceGroup: SpaceGroupsActionsSlotPayload | null;
}

export const AssignEmployeeGroupsToSpaceGroupDialog = ({
  open,
  onCancel,
  selectedSpaceGroup,
}: Props) => {
  const { t } = useTranslation(['spaceGroups', 'spaces']);

  const { directoryToken } = useUserStore();

  const [selectedEmployeeGroups, setSelectedEmployeeGroups] = useState<
    string[]
  >([]);

  const [searchState, setSearchState] = useSearchState();

  const {
    page,
    perPage,
    changePage,
    changePerPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const { results, isLoading, isFetching, error } = useMeilisearch({
    fetch: fetchEmployeesGroups,
    page,
    perPage,
    queryKey: employeesGroupsKeys.list(
      directoryToken ?? '',
      perPage,
      page,
      searchState,
    ),
    searchState,
    enabled: open,
  });

  useEffect(() => {
    if (!results) return;
    setTotalItems(results.estimatedTotalHits);
  }, [results]);

  const handleOnCancel = () => {
    onCancel?.();
    setSelectedEmployeeGroups([]);
  };

  const {
    mutate: assignEmployeeGroupsToSpaceGroup,
    isPending: isAssigningEmployeeGroupsToSpaceGroup,
  } = useAssignEmployeeGroupsToSpaceGroup(handleOnCancel);

  const handleChangeQuery = (query: string) => {
    setSearchState(prevState => ({
      ...prevState,
      q: query,
    }));
  };

  const handleUpdateSelectedEmployeeGroups = (employeeGroupToken: string) => {
    setSelectedEmployeeGroups(prevState => {
      if (prevState.includes(employeeGroupToken)) {
        return prevState.filter(token => token !== employeeGroupToken);
      }
      return [...prevState, employeeGroupToken];
    });
  };

  const handleAssignEmployeeGroupsToSpaceGroup = () => {
    if (!selectedSpaceGroup) {
      return;
    }

    assignEmployeeGroupsToSpaceGroup({
      spaceGroupToken: selectedSpaceGroup?.token,
      employeeGroupTokens: selectedEmployeeGroups,
    });
  };

  const isEmployeeGroupAlreadyAssignedToSpaceGroup = (
    employeeGroupToken: string,
  ) => {
    return selectedSpaceGroup?.employeesGroupsTokens.includes(
      employeeGroupToken,
    );
  };

  return (
    <Dialog
      open={open}
      title={t('spaceGroups:common.manageEmployeeGroupsDialog.title')}
      onCancel={handleOnCancel}
      onConfirm={handleAssignEmployeeGroupsToSpaceGroup}
      isConfirmButtonSubmitting={isAssigningEmployeeGroupsToSpaceGroup}
      isConfirmButtonDisabled={selectedEmployeeGroups.length === 0}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="body2">
          {t('spaceGroups:common.manageEmployeeGroupsDialog.message')}
        </Typography>

        <Query
          searchAsType
          disabled={false}
          onChange={handleChangeQuery}
          sx={{
            minWidth: 200,
          }}
          value={searchState.q}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}>
          {results?.hits?.map(employeeGroup => (
            <Tooltip
              title={t(
                'spaceGroups:common.manageEmployeeGroupsDialog.alreadyAssigned',
              )}
              placement="right"
              disableFocusListener={
                !isEmployeeGroupAlreadyAssignedToSpaceGroup(employeeGroup.token)
              }
              disableHoverListener={
                !isEmployeeGroupAlreadyAssignedToSpaceGroup(employeeGroup.token)
              }>
              <span>
                <CardWithContent
                  key={employeeGroup.token}
                  isSelected={selectedEmployeeGroups.includes(
                    employeeGroup.token,
                  )}
                  invalid={isEmployeeGroupAlreadyAssignedToSpaceGroup(
                    employeeGroup.token,
                  )}
                  onClick={() => {
                    handleUpdateSelectedEmployeeGroups(employeeGroup.token);
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={
                        selectedEmployeeGroups.includes(employeeGroup.token) ||
                        isEmployeeGroupAlreadyAssignedToSpaceGroup(
                          employeeGroup.token,
                        )
                      }
                      disabled={isEmployeeGroupAlreadyAssignedToSpaceGroup(
                        employeeGroup.token,
                      )}
                    />
                    <Box>
                      <Typography variant="body2">
                        {employeeGroup.name}
                      </Typography>
                    </Box>
                  </Box>
                </CardWithContent>
              </span>
            </Tooltip>
          ))}

          <DataStatusIndicator
            dataLength={results?.hits?.length ?? 0}
            isFetching={isLoading}
            error={error?.response?.data?.message}
          />

          <CompactPagination
            disabled={isLoading}
            isFetching={isFetching}
            page={page}
            changePage={changePage}
            handlePerPageChange={changePerPage}
            nextDisabled={nextDisabled}
            nextPage={nextPage}
            prevDisabled={prevDisabled}
            prevPage={prevPage}
            perPage={perPage}
            totalPages={totalPages}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
